import * as THREE from 'three';
import { AdsorbOrthogonalLine } from '@/editor/manager/entityManager/adsorb/orthogonalAdsorb';
import { disposeObject } from '@/editor/entities/utils';
import { VectorPointUtils } from '@/editor/math/VectorPointUtils';
import { hasGeometryAndMaterial } from '@/editor/utils/object3d';

const LIMITATION = {
  min: 1,
  max: 1e8 - 1,
  maxLength: 8
} as const;

export class PDFPreviewRuler {
  private _model: THREE.Group = new THREE.Group();
  private _start: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private _end: THREE.Vector3 = new THREE.Vector3(0, 0, 0);
  private _scale: number = 1;
  private _value: number = 4000;
  private _rulerStartLine: THREE.Line;
  private _rulerEndLine: THREE.Line;
  private _rulerBody: THREE.Mesh;
  private _startHandler: THREE.Group;
  private _endHandler: THREE.Group;
  private _dimensionGroup: THREE.Group;
  private _dimensionLine: THREE.Line;
  private _dimensionStartAssistLine: THREE.Line;
  private _dimensionStartAssistSlashLine: THREE.Line;
  private _dimensionEndAssistLine: THREE.Line;
  private _dimensionEndAssistSlashLine: THREE.Line;
  private _camera: THREE.Camera;
  private _renderer: THREE.WebGLRenderer;
  private _inputContainer?: HTMLDivElement;
  private _inputElement?: HTMLInputElement;
  private _parentElement: HTMLElement | null = null;
  private _visible: boolean = true;
  private _adsorbOrthogonalLine = new AdsorbOrthogonalLine(0.5);

  constructor(camera: THREE.Camera, renderer: THREE.WebGLRenderer) {
    this._camera = camera;
    this._renderer = renderer;
    this._rulerStartLine = new THREE.Line();
    this._rulerEndLine = new THREE.Line();
    this._rulerBody = new THREE.Mesh();
    this._rulerBody.userData = {
      intersectable: true,
      type: 'body'
    };
    this._startHandler = new THREE.Group();
    this._endHandler = new THREE.Group();
    this._dimensionGroup = new THREE.Group();
    this._dimensionLine = new THREE.Line();
    this._dimensionStartAssistLine = new THREE.Line();
    this._dimensionStartAssistSlashLine = new THREE.Line();
    this._dimensionEndAssistLine = new THREE.Line();
    this._dimensionEndAssistSlashLine = new THREE.Line();
    this._initial();
    this._initialDimension();
    this._createInputElement();
    this._model.add(
      this._rulerStartLine,
      this._rulerEndLine,
      this._rulerBody,
      this._startHandler,
      this._endHandler,
      this._dimensionGroup
    );
    this.update();
  }

  get value() {
    return this._value;
  }

  get distance() {
    return this._start.distanceTo(this._end);
  }

  setVisible(visible: boolean) {
    if (this._visible === visible) return;
    this._visible = visible;
    if (visible) {
      this._model.visible = true;
      if (this._inputContainer) {
        this._inputContainer.style.display = 'block';
      }
    } else {
      this._model.visible = false;
      if (this._inputContainer) {
        this._inputContainer.style.display = 'none';
      }
    }
  }

  public setParentElement(element: HTMLElement) {
    this._parentElement = element;
    if (this._inputContainer && !this._parentElement.contains(this._inputContainer)) {
      this._parentElement.appendChild(this._inputContainer);
      this._updateInputPosition();
    }
  }

  private get dimLegInfo() {
    const direction = new THREE.Vector3().subVectors(this._end, this._start);
    const dir = direction.clone().normalize();
    const perp = new THREE.Vector3(-dir.y, dir.x, 0);
    const offset = -1.35 * this._scale;
    const dimStart = this._start.clone().addScaledVector(perp, offset);
    const dimEnd = this._end.clone().addScaledVector(perp, offset);
    return {
      start: dimStart,
      end: dimEnd,
      perp
    };
  }

  private _initial() {
    const lineMaterial = new THREE.LineBasicMaterial({
      color: 0xf28600,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide
    });
    const bodyMaterial = new THREE.MeshBasicMaterial({
      color: 0xf28600,
      side: THREE.DoubleSide,
      opacity: 0.5,
      transparent: true
    });
    const dotMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      side: THREE.DoubleSide,
      opacity: 1,
      transparent: false
    });
    this._rulerStartLine.material = lineMaterial;
    this._rulerEndLine.material = lineMaterial;
    this._rulerBody.material = bodyMaterial;
    this._startHandler.userData = {
      intersectable: true,
      type: 'start'
    };
    this._endHandler.userData = {
      intersectable: true,
      type: 'end'
    };
    this._createHandler(this._startHandler, dotMaterial, lineMaterial);
    this._createHandler(this._endHandler, dotMaterial, lineMaterial);
  }

  private _initialDimension() {
    const dimensionMaterial = new THREE.LineBasicMaterial({
      color: 0x262829,
      transparent: true,
      opacity: 1,
      side: THREE.DoubleSide
    });
    dimensionMaterial.depthTest = false;
    dimensionMaterial.depthWrite = false;

    for (const line of [
      this._dimensionLine,
      this._dimensionStartAssistLine,
      this._dimensionStartAssistSlashLine,
      this._dimensionEndAssistLine,
      this._dimensionEndAssistSlashLine
    ]) {
      line.material = dimensionMaterial;
      line.renderOrder = 1;
      this._dimensionGroup.add(line);
    }
  }

  get model() {
    return this._model;
  }

  public update() {
    this._updateRuler();
    this._updateHandler();
    this._updateDimension();
    this._updateInputPosition();
  }

  public setCameraScale(zoom: number) {
    this._scale = zoom === 0 ? 1 : 1 / zoom;
    this._adsorbOrthogonalLine.setMinAdsorbDistance(0.5 * this._scale);
    this.update();
  }

  updateStartPoint(position: { x: number; y: number }) {
    this._adsorbOrthogonalLine.updateOrthogonalLine(VectorPointUtils.vectorToPoint(this._end));
    let isAdsorbed = false;
    const adsorbResult = this._adsorbOrthogonalLine.orthogonalAdsorbResult(position);
    if (adsorbResult.horizontalAdsorbResult) {
      const horizontalAdsorbPoint = this._adsorbOrthogonalLine.pointOnHorizontalLine(position);
      if (horizontalAdsorbPoint) {
        this._start.set(horizontalAdsorbPoint.x, horizontalAdsorbPoint.y, 0);
        isAdsorbed = true;
      }
    }

    if (adsorbResult.verticalAdsorbResult) {
      const verticalAdsorbPoint = this._adsorbOrthogonalLine.pointOnVerticalLine(position);
      if (verticalAdsorbPoint) {
        this._start.set(verticalAdsorbPoint.x, verticalAdsorbPoint.y, 0);
        isAdsorbed = true;
      }
    }

    if (!isAdsorbed) {
      this._start.set(position.x, position.y, 0);
    }
    this.update();
  }

  updateEndPoint(position: { x: number; y: number }) {
    this._adsorbOrthogonalLine.updateOrthogonalLine(VectorPointUtils.vectorToPoint(this._start));
    let isAdsorbed = false;
    const adsorbResult = this._adsorbOrthogonalLine.orthogonalAdsorbResult(position);
    if (adsorbResult.horizontalAdsorbResult) {
      const horizontalAdsorbPoint = this._adsorbOrthogonalLine.pointOnHorizontalLine(position);
      if (horizontalAdsorbPoint) {
        this._end.set(horizontalAdsorbPoint.x, horizontalAdsorbPoint.y, 0);
        isAdsorbed = true;
      }
    }

    if (adsorbResult.verticalAdsorbResult) {
      const verticalAdsorbPoint = this._adsorbOrthogonalLine.pointOnVerticalLine(position);
      if (verticalAdsorbPoint) {
        this._end.set(verticalAdsorbPoint.x, verticalAdsorbPoint.y, 0);
        isAdsorbed = true;
      }
    }

    if (!isAdsorbed) {
      this._end.set(position.x, position.y, 0);
    }

    this.update();
  }

  moveBody(startPos: { x: number; y: number }, endPos: { x: number; y: number }) {
    const moveVector = new THREE.Vector3(endPos.x - startPos.x, endPos.y - startPos.y, 0);
    this._start.add(moveVector);
    this._end.add(moveVector);
    this.update();
  }

  private _updateInputPosition() {
    if (!this._inputContainer) return;

    const camera = this._camera;
    const renderer = this._renderer;
    const canvasRect = renderer.domElement.getBoundingClientRect();
    const { start, end } = this.dimLegInfo;
    const midPoint = start.clone().lerp(end, 0.5);
    const projected = midPoint.clone().project(camera);

    const x = ((projected.x + 1) / 2) * canvasRect.width;
    const y = ((-projected.y + 1) / 2) * canvasRect.height;

    this._inputContainer.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  }

  private _updateHandler() {
    const radius = 0.25 * this._scale;

    const dotGeometry = new THREE.CircleGeometry(radius, 32);
    const startDotMesh = this._startHandler.children[0];
    if (hasGeometryAndMaterial(startDotMesh)) {
      startDotMesh.geometry.dispose();
      startDotMesh.geometry = dotGeometry;
    }
    const borderGeometry = new THREE.RingGeometry(radius * 0.95, radius, 32);
    const startBorderMesh = this._startHandler.children[1];
    if (hasGeometryAndMaterial(startBorderMesh)) {
      startBorderMesh.geometry.dispose();
      startBorderMesh.geometry = borderGeometry;
    }

    const endDotMesh = this._endHandler.children[0];
    if (hasGeometryAndMaterial(endDotMesh)) {
      endDotMesh.geometry.dispose();
      endDotMesh.geometry = dotGeometry;
    }
    const endBorderMesh = this._endHandler.children[1];
    if (hasGeometryAndMaterial(endBorderMesh)) {
      endBorderMesh.geometry.dispose();
      endBorderMesh.geometry = borderGeometry;
    }

    this._startHandler.position.copy(this._start);
    this._startHandler.position.setZ(0.1);
    this._endHandler.position.copy(this._end);
    this._endHandler.position.setZ(0.1);
  }

  private _createHandler(group: THREE.Group, dotMaterial: THREE.Material, borderMaterial: THREE.Material) {
    const dotMesh = new THREE.Mesh();
    const borderMesh = new THREE.Mesh();
    dotMesh.userData = {
      intersectable: false,
      type: 'handler-dot'
    };
    borderMesh.userData = {
      intersectable: false,
      type: 'handler-border'
    };
    dotMesh.material = dotMaterial;
    borderMesh.material = borderMaterial;
    group.add(dotMesh, borderMesh);
  }

  private _updateRuler() {
    const rulerThickness = this._scale;
    const rulerAngle = this._getRotateAngle();

    const zAxis = new THREE.Vector3(0, 0, 1);
    const rulerSideVec = new THREE.Vector3(0, rulerThickness, 0).applyAxisAngle(zAxis, rulerAngle);
    const startPoint = this._start.clone();
    const endPoint = this._end.clone();
    const startLineGeometry = new THREE.BufferGeometry();

    startLineGeometry.setFromPoints([startPoint.clone().add(rulerSideVec), startPoint.clone().sub(rulerSideVec)]);
    this._rulerStartLine.geometry.dispose();
    this._rulerStartLine.geometry = startLineGeometry;

    const endLineGeometry = new THREE.BufferGeometry();

    endLineGeometry.setFromPoints([endPoint.clone().add(rulerSideVec), endPoint.clone().sub(rulerSideVec)]);
    this._rulerEndLine.geometry.dispose();
    this._rulerEndLine.geometry = endLineGeometry;

    const length = endPoint.distanceTo(startPoint);
    const bodyGeometry = new THREE.PlaneGeometry(length, rulerThickness, 1);
    bodyGeometry.rotateZ(rulerAngle);
    this._rulerBody.geometry.dispose();
    this._rulerBody.geometry = bodyGeometry;

    // 让平面沿着刻度尺方向摆放
    const center = startPoint.clone().lerp(endPoint, 0.5);
    this._rulerBody.position.copy(center);
  }

  private _getRotateAngle() {
    const direction = new THREE.Vector3().subVectors(this._end, this._start).normalize();
    const angle = Math.atan2(direction.y, direction.x);
    return angle;
  }

  private _updateDimension() {
    const direction = new THREE.Vector3().subVectors(this._end, this._start);
    const length = direction.length();

    if (length === 0) {
      this._dimensionGroup.visible = false;
      return;
    }

    this._dimensionGroup.visible = true;
    const { start: dimStart, end: dimEnd, perp } = this.dimLegInfo;

    const dimGeometry = new THREE.BufferGeometry().setFromPoints([dimStart, dimEnd]);
    this._dimensionLine.geometry.dispose();
    this._dimensionLine.geometry = dimGeometry;

    this._updateDimensionAssistLines(dimStart, dimEnd, perp);
  }

  private _updateDimensionAssistLines(dimStart: THREE.Vector3, dimEnd: THREE.Vector3, perp: THREE.Vector3) {
    const tickSize = 0.3 * this._scale;
    const sqrt2Half = Math.SQRT2 / 2;
    const diagonalVector = new THREE.Vector3((-perp.x - perp.y) * sqrt2Half, (perp.x - perp.y) * sqrt2Half, 0);

    const tick1Start = dimStart.clone().addScaledVector(perp, -tickSize);
    const tick1End = dimStart.clone().addScaledVector(perp, tickSize);
    const tick1Geometry = new THREE.BufferGeometry().setFromPoints([tick1Start, tick1End]);
    this._dimensionStartAssistLine.geometry.dispose();
    this._dimensionStartAssistLine.geometry = tick1Geometry;

    const slashStart = dimStart.clone().addScaledVector(diagonalVector, (-tickSize * 10) / 12);
    const slashEnd = dimStart.clone().addScaledVector(diagonalVector, (tickSize * 10) / 12);
    const slashGeometry = new THREE.BufferGeometry().setFromPoints([slashStart, slashEnd]);
    this._dimensionStartAssistSlashLine.geometry.dispose();
    this._dimensionStartAssistSlashLine.geometry = slashGeometry;

    const tick2Start = dimEnd.clone().addScaledVector(perp, -tickSize);
    const tick2End = dimEnd.clone().addScaledVector(perp, tickSize);
    const tick2Geometry = new THREE.BufferGeometry().setFromPoints([tick2Start, tick2End]);
    this._dimensionEndAssistLine.geometry.dispose();
    this._dimensionEndAssistLine.geometry = tick2Geometry;

    const endSlashStart = dimEnd.clone().addScaledVector(diagonalVector, (-tickSize * 10) / 12);
    const endSlashEnd = dimEnd.clone().addScaledVector(diagonalVector, (tickSize * 10) / 12);
    const endSlashGeometry = new THREE.BufferGeometry().setFromPoints([endSlashStart, endSlashEnd]);
    this._dimensionEndAssistSlashLine.geometry.dispose();
    this._dimensionEndAssistSlashLine.geometry = endSlashGeometry;
  }

  private _createInputElement() {
    const inputElement = document.createElement('input');
    inputElement.type = 'number';
    inputElement.classList.add('no-spinner', 'pdf-ruler-input');

    const container = document.createElement('div');
    container.classList.add('pdf-ruler-input-wrapper');

    container.append(inputElement);

    inputElement.value = this._value.toFixed(0);
    inputElement.addEventListener('keydown', this._handleInputKeyDown);
    inputElement.addEventListener('blur', this._commitInputValue);
    inputElement.addEventListener('change', this._commitInputValue);

    inputElement.min = LIMITATION.min.toString(10);
    inputElement.max = LIMITATION.max.toString(10);

    this._inputElement = inputElement;
    this._inputContainer = container;
  }

  private _handleInputKeyDown = (event: KeyboardEvent) => {
    if (!this._inputElement) return;
    switch (event.key) {
      case 'Enter': {
        event.preventDefault();
        this._commitInputValue();
        this._inputElement.blur();
        break;
      }
      case '.':
      case '-':
      case '+':
      case 'e': {
        event.preventDefault();
        break;
      }
      default: {
        const isInputDigit = /\d/.test(event.key);
        const value = this._inputElement.value;
        if (isInputDigit && value.length >= LIMITATION.maxLength) {
          event.preventDefault();
        }
        if (value.length > LIMITATION.maxLength) {
          this._inputElement.value = value.slice(0, LIMITATION.maxLength);
          event.preventDefault();
        }
      }
    }
  };

  private _commitInputValue = () => {
    if (!this._inputElement) return;
    const parsed = Number.parseInt(this._inputElement.value, 10);

    if (Number.isNaN(parsed) || parsed < LIMITATION.min || parsed > LIMITATION.max) {
      this._inputElement.value = '4000';
      this._value = 4000;
      return;
    }

    this._value = parsed;
    this._inputElement.value = this._value.toFixed(0);
  };

  public dispose() {
    if (this._inputElement) {
      this._inputElement.removeEventListener('keydown', this._handleInputKeyDown);
      this._inputElement.removeEventListener('blur', this._commitInputValue);
      this._inputElement.removeEventListener('change', this._commitInputValue);
    }

    if (this._inputContainer && this._parentElement && this._parentElement.contains(this._inputContainer)) {
      this._parentElement.removeChild(this._inputContainer);
    }

    const objectsToDispose = [
      this._dimensionLine,
      this._dimensionStartAssistLine,
      this._dimensionStartAssistSlashLine,
      this._dimensionEndAssistLine,
      this._dimensionEndAssistSlashLine,
      this._rulerStartLine,
      this._rulerEndLine,
      this._rulerBody,
      ...this._startHandler.children,
      ...this._endHandler.children
    ];

    objectsToDispose.forEach((obj) => {
      if (obj instanceof THREE.Mesh || obj instanceof THREE.Line) {
        disposeObject(obj);
      }
    });

    this._model.clear();
  }
}
